import {
  FETCH_QUEUE,
  FETCH_QUEUE_SUCCESS,
  FETCH_QUEUE_FAILURE,
  FETCH_CUSTOMER_ALERTS,
  FETCH_CUSTOMER_ALERTS_SUCCESS,
  FETCH_CUSTOMER_ALERTS_FAILURE,
  SET_FILTERS,
  SET_SORT,
  SET_PAGE,
  SET_SIZE,
  SET_EXPANDED_ROW_KEYS,
  CREATE_CASES_FOR_CUSTOMERS,
  MERGE_CASES_FOR_CUSTOMERS,
  BULK_CASE_OUTCOME,
  CLEAR_BULK_CASE_OUTCOME,
  DOWNLOAD_FRAML_QUEUE_EXPORT,
  DOWNLOAD_FRAML_QUEUE_EXPORT_SUCCESS,
  DOWNLOAD_FRAML_QUEUE_EXPORT_FAIL
} from '../../actions/framl/landing';

export const initialState = {
  customers: [],
  summary: {
    totalCustomers: 0,
    totalAlerts: 0,
    fraudCount: 0,
    amlCount: 0,
    crossDomainCount: 0,
    lastComputedAt: null
  },
  filters: {
    source: '',
    riskBand: '',
    status: '',
    search: ''
  },
  sort: { sortField: 'latestAlertDate', sortDir: 'desc' },
  pagination: { page: 0, size: 10, total: 0 },
  customerAlerts: {},
  // FIN-12887: per-customer paging meta for the row-expand ({ total, page, size }).
  // Kept separate so customerAlerts[key] stays a flat array for the bulk-action paths.
  customerAlertsMeta: {},
  // FIN-13005 — customerKey -> error message when the expand fetch FAILED (vs returned empty)
  customerAlertsError: {},
  // FIN-13938 — customerKeys of expanded queue rows (kept across route changes).
  expandedRowKeys: [],
  // FIN-13958 — a Create Case / Merge to Case across customers is in flight (guards double submit),
  // and its ONE folded outcome for the page to report (null once shown).
  // Per kind (review L3): one action's outcome must not unlock the other.
  createInFlight: false,
  mergeInFlight: false,
  bulkCaseOutcome: null,
  loading: false,
  error: null,
  exportInProgress: false
};

export default function reducer(state = initialState, { type, payload }) {
  switch (type) {
    case FETCH_QUEUE:
      return { ...state, loading: true, error: null };
    case FETCH_QUEUE_SUCCESS:
      return {
        ...state,
        loading: false,
        customers: payload.customers,
        summary: {
          totalCustomers: payload.totalCustomers,
          totalAlerts: payload.totalAlerts,
          fraudCount: payload.fraudCount,
          amlCount: payload.amlCount,
          crossDomainCount: payload.crossDomainCount,
          lastComputedAt: payload.lastComputedAt ?? null
        },
        pagination: payload.pagination
      };
    case FETCH_QUEUE_FAILURE:
      return { ...state, loading: false, error: payload };
    case FETCH_CUSTOMER_ALERTS:
      // FIN-13005 — clear any previous error for this key so a retry starts clean.
      return {
        ...state,
        customerAlertsError: {
          ...state.customerAlertsError,
          [payload.customerKey]: undefined
        }
      };
    case FETCH_CUSTOMER_ALERTS_FAILURE:
      // FIN-13005 — record the failure per customer. The panel reads this to distinguish
      // "couldn't load" from "no underlying alerts"; without it both render as (0).
      return {
        ...state,
        customerAlertsError: {
          ...state.customerAlertsError,
          [payload.customerKey]: payload.error
        }
      };
    case FETCH_CUSTOMER_ALERTS_SUCCESS:
      return {
        ...state,
        customerAlertsError: {
          ...state.customerAlertsError,
          [payload.customerKey]: undefined
        },
        customerAlerts: {
          ...state.customerAlerts,
          [payload.customerKey]: payload.alerts
        },
        customerAlertsMeta: {
          ...state.customerAlertsMeta,
          [payload.customerKey]: {
            total: payload.total,
            page: payload.page,
            size: payload.size
          }
        }
      };
    // FIN-13017 — every filter/sort change must return to page 0.
    //
    // The saga watches SET_FILTERS/SET_SORT and refetches by `select(getPagination)`, so a
    // retained offset is sent against a freshly-filtered result set. On page 2+ with a filter
    // that narrows the queue to fewer rows than the offset, the request overshoots and the grid
    // renders empty even though the filter matched. The reducer runs before the saga watcher,
    // so resetting here is what the refetch reads.
    //
    // This is not search-only: search, the Source/Risk Band/Status dropdowns, the header tiles
    // (onTileFilter -> setFilters({ source })) and both sort controls all funnel through these
    // two actions, so all six were affected. SET_SIZE is NOT included — handleSizeChange already
    // dispatches setPage(0) before setSize, and SET_PAGE is the user deliberately paging.
    // FIN-13938 — a filter/sort change yields a different row set, so expanded rows are collapsed.
    case SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...payload },
        pagination: { ...state.pagination, page: 0 },
        expandedRowKeys: []
      };
    case SET_SORT:
      return {
        ...state,
        sort: payload,
        pagination: { ...state.pagination, page: 0 },
        expandedRowKeys: []
      };
    case SET_EXPANDED_ROW_KEYS:
      return { ...state, expandedRowKeys: payload.keys || [] };
    case SET_PAGE:
      return {
        ...state,
        pagination: { ...state.pagination, page: payload.page }
      };
    case SET_SIZE:
      return {
        ...state,
        pagination: { ...state.pagination, size: payload.size }
      };
    case CREATE_CASES_FOR_CUSTOMERS:
      return { ...state, createInFlight: true, bulkCaseOutcome: null };
    case MERGE_CASES_FOR_CUSTOMERS:
      return { ...state, mergeInFlight: true, bulkCaseOutcome: null };
    case BULK_CASE_OUTCOME:
      return {
        ...state,
        ...(payload && payload.kind === 'merge'
          ? { mergeInFlight: false }
          : { createInFlight: false }),
        bulkCaseOutcome: payload
      };
    case CLEAR_BULK_CASE_OUTCOME:
      return { ...state, bulkCaseOutcome: null };
    case DOWNLOAD_FRAML_QUEUE_EXPORT:
      return { ...state, exportInProgress: true };
    case DOWNLOAD_FRAML_QUEUE_EXPORT_SUCCESS:
    case DOWNLOAD_FRAML_QUEUE_EXPORT_FAIL:
      return { ...state, exportInProgress: false };
    default:
      return state;
  }
}
